import { libelleAccroche } from './barre-trace';
import { formaterDistance } from '../../src/partage/utm.js';
import { libelleStatutParcelle } from '../../src/partage/libelles';
import type { EtatTrace } from './trace';
import type { SelectionCarte } from './carte';

/**
 * LES VALEURS DE LA BARRE D'ÉTAT — un seul calcul pour les DEUX écrans.
 *
 * ⚠️ La coquille de consultation (`carte-cadastrale.tsx`) et l'atelier
 * (`atelier.tsx`) affichent la même barre (D.944). Calculer « outil /
 * accrochage / mesure » dans chacun en ferait deux implémentations qui
 * divergent au premier correctif — le motif le plus cher de cet écosystème.
 *
 * `null` signifie « — » : la barre affiche toujours la clé, jamais le vide.
 */
export function valeursTraceBarre(etat: EtatTrace, mesureSeule = false): {
  outil: string | null; accroche: string | null; mesure: string | null;
} {
  if (etat.mode === 'inactif') return { outil: null, accroche: null, mesure: null };
  const outil = etat.mode === 'modifier'
    ? `modification d’emprise${etat.corrige ? ` — ${etat.corrige.identifiant}` : ''}`
    : mesureSeule
      ? `mesure — ${etat.nature === 'surface' ? 'surface' : 'distance'}`
      : `tracé — ${etat.nature === 'surface' ? 'emprise' : etat.nature === 'ligne' ? 'ligne' : 'point'}`;
  const accroche = etat.accroche
    ? `${libelleAccroche(etat.accroche.nature)} à ${etat.accroche.distanceM.toFixed(2)} m`
    : 'aucun sur le dernier point';
  const mesure = etat.mesureLineaireM > 0 ? formaterDistance(etat.mesureLineaireM) : null;
  return { outil, accroche, mesure };
}

/**
 * SÉLECTION ET OPPOSABILITÉ — celles de l'OBJET désigné, jamais un état
 * global du plan (PC§35.4 : géométrie cadastrale ≠ limite opposable).
 */
export function valeursSelectionBarre(
  sel: SelectionCarte | null,
  collectivite: { nom: string } | null,
): { texte: string | null; opposabilite: string | null } {
  if (sel) {
    const p = sel.proprietes as Record<string, unknown>;
    if (sel.couche === 'parcelles') {
      return { texte: `parcelle ${sel.identifiant}`, opposabilite: libelleStatutParcelle(p.statut) };
    }
    if (sel.couche === 'batiments') {
      return {
        texte: `bâtiment ${sel.identifiant}`,
        opposabilite: p.rattache
          ? 'bâti rattaché à une parcelle'
          : 'bâti non rattaché — aucune parcelle ne le couvre',
      };
    }
    if (sel.couche === 'sections') {
      return {
        texte: `section ${String(p.code ?? sel.identifiant)}`,
        opposabilite: p.origine === 'voie'
          ? 'découpage borné par les rues — non opposable'
          : 'découpage au carroyage — non opposable',
      };
    }
  }
  if (collectivite) return { texte: `collectivité ${collectivite.nom}`, opposabilite: null };
  return { texte: null, opposabilite: null };
}
